import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { ArrowRight, ArrowLeft, ShieldCheck, Plus, X, Check } from "lucide-react";

import Field from "@/components/form/Field";
import MultiSelect from "@/components/form/MultiSelect";
import PitchDeckDropzone from "@/components/form/PitchDeckDropzone";
import SectionBanner from "@/components/form/SectionBanner";
import SectionHeading from "@/components/common/SectionHeading";
import Reveal from "@/components/common/Reveal";
import PageHero from "@/components/layout/PageHero";
import { VisualSection } from "@/components/layout/visualLayout";
import ASSETS from "@/lib/assets";
import api from "@/lib/api";
import { EMAIL_RE, fieldClass } from "@/lib/validation";

const URL_RE = /^https?:\/\/.+/i;

const SECTORS = [
  "AI", "SaaS", "FinTech", "HealthTech", "ClimateTech", "EdTech",
  "Cybersecurity", "HRTech", "Mobility", "Consumer", "Creator Economy", "Other",
];
const BUSINESS_MODELS = [
  "B2B", "B2C", "B2B2C", "Marketplace", "SaaS", "Hardware", "DeepTech", "AI", "Other",
];
const STAGES = ["idea", "mvp", "pre_seed", "seed", "series_a", "growth"];
const CURRENCIES = ["EUR", "USD", "GBP", "CHF"];
const COUNTRIES = [
  "Germany", "Austria", "Switzerland", "France", "Spain", "Italy",
  "Netherlands", "Belgium", "Denmark", "Sweden", "Norway", "Finland",
  "United Kingdom", "Ireland", "Portugal", "Poland", "Czechia",
  "United States", "Canada", "Other",
];

const emptyFounder = () => ({
  first_name: "", last_name: "", email: "", linkedin: "", role: "",
  technical: false, female_led: false, diverse_team: false,
  previous_startup_experience: false,
});

const TOTAL_STEPS = 5;

const WIZARD_STEPS_DEF = [
  { id: 1, labelKey: "step1Label" },
  { id: 2, labelKey: "step2Label" },
  { id: 3, labelKey: "step3Label" },
  { id: 4, labelKey: "step4Label" },
  { id: 5, labelKey: "step5Label" },
];

/* ── Validation ─────────────────────────────────────────────── */
function validateStep1(company) {
  const e = {};
  if (!company.name.trim())         e["company.name"]        = "Pflichtfeld.";
  if (!company.website.trim())      e["company.website"]     = "Pflichtfeld.";
  else if (!URL_RE.test(company.website.trim())) e["company.website"] = "Bitte eine gültige URL eingeben (https://...).";
  if (!company.founded_year)        e["company.founded_year"]= "Pflichtfeld.";
  else { const y = Number(company.founded_year); if (y < 1990 || y > 2030) e["company.founded_year"] = "Zwischen 1990 und 2030."; }
  if (!company.country)             e["company.country"]     = "Bitte ein Land auswählen.";
  if (!company.city.trim())         e["company.city"]        = "Pflichtfeld.";
  if (!company.sectors.length)      e["company.sectors"]     = "Bitte mindestens einen Sektor wählen.";
  if (!company.one_liner.trim())    e["company.one_liner"]   = "Pflichtfeld.";
  if (!company.description.trim())  e["company.description"] = "Pflichtfeld.";
  else if (company.description.trim().length < 100) e["company.description"] = "Bitte mindestens 100 Zeichen eingeben.";
  return e;
}

function validateStep2(founders) {
  const e = {};
  founders.forEach((fn, i) => {
    if (!fn.first_name.trim()) e[`founder.${i}.first_name`] = "Pflichtfeld.";
    if (!fn.last_name.trim())  e[`founder.${i}.last_name`]  = "Pflichtfeld.";
    if (!fn.email.trim())      e[`founder.${i}.email`]      = "Pflichtfeld.";
    else if (!EMAIL_RE.test(fn.email.trim())) e[`founder.${i}.email`] = "Ungültige E-Mail.";
    if (!fn.role.trim())       e[`founder.${i}.role`]       = "Pflichtfeld.";
  });
  return e;
}

function validateStep3(details, funding) {
  const e = {};
  if (!details.business_models.length) e["details.business_models"] = "Bitte mindestens ein Modell wählen.";
  if (!funding.ask_amount)             e["funding.ask_amount"]      = "Pflichtfeld.";
  else if (Number(funding.ask_amount) <= 0) e["funding.ask_amount"] = "Bitte einen positiven Betrag eingeben.";
  return e;
}

function validateByStep(step, form, deck) {
  if (step === 1) return validateStep1(form.company);
  if (step === 2) return validateStep2(form.founders);
  if (step === 3) return validateStep3(form.details, form.funding);
  if (step === 4) return deck ? {} : { deck: "Bitte ein Pitch Deck hochladen." };
  return {};
}

/* ── Wizard Progress Bar ─────────────────────────────────────── */
function WizardProgress({ wizardStep, steps }) {
  return (
    <div className="flex items-center w-full mb-10" data-testid="wizard-progress">
      {steps.map((step, i) => {
        const isDone   = wizardStep > step.id;
        const isActive = wizardStep === step.id;
        return (
          <div key={step.id} className="flex items-center" style={{ flex: i < steps.length - 1 ? "1 1 0%" : "0 0 auto" }}>
            <div className="flex flex-col items-center gap-1.5 shrink-0">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 ${
                isDone   ? "bg-raspberry text-white" :
                isActive ? "bg-white border-2 border-raspberry text-raspberry" :
                           "bg-white border border-sandBeige text-warmGrey"
              }`}>
                {isDone ? <Check size={14} strokeWidth={2.8} /> : step.id}
              </div>
              <span className={`hidden sm:block text-xs leading-none text-center whitespace-nowrap transition-colors duration-200 ${
                isActive ? "text-raspberry font-medium" : isDone ? "text-cocoa" : "text-warmGrey"
              }`}>{step.label}</span>
            </div>
            {i < steps.length - 1 && (
              <div className={`flex-1 h-px mx-2 transition-colors duration-300 ${isDone ? "bg-raspberry" : "bg-sandBeige"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── Main component ──────────────────────────────────────────── */
export default function SubmitStartup() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [wizardStep, setWizardStep] = useState(1);
  const [form, setForm] = useState({
    company: {
      name: "", website: "", linkedin: "", founded_year: "",
      country: "", city: "", sectors: [], one_liner: "", description: "",
    },
    founders: [emptyFounder()],
    details: { stage: "seed", business_models: [] },
    funding: {
      ask_amount: "", ask_currency: "EUR",
      raised_so_far: "", valuation: "", runway_months: "",
      mrr: "", arr: "", has_pilot_customers: false,
      customer_count: "", use_of_funds: "",
    },
    consents: {
      pitch_deck_upload: false, privacy_policy: false, ai_analysis: false,
      no_auto_forward: false, no_guarantee: false, gdpr_data_processing: false,
      ip_ownership: false, nda_confidentiality: false,
    },
  });
  const [deck,       setDeck]       = useState(null);
  const [deckError,  setDeckError]  = useState(null);
  const [touched,    setTouched]    = useState({});       // { [fieldKey]: true }
  const [attempted,  setAttempted]  = useState(false);   // true after first failed Next
  const [state,      setState]      = useState({ status: "idle", message: "" });
  const [result,     setResult]     = useState(null);

  const wizardSteps = WIZARD_STEPS_DEF.map((s) => ({
    ...s,
    label: t(`submit.wizard.${s.labelKey}`),
  }));

  /* ── field helpers ─────────────────────────────────────────── */
  const updateCompany = (key, val) => setForm((f) => ({ ...f, company: { ...f.company, [key]: val } }));
  const updateFunding = (key, val) => setForm((f) => ({ ...f, funding: { ...f.funding, [key]: val } }));
  const updateDetails = (key, val) => setForm((f) => ({ ...f, details: { ...f.details, [key]: val } }));
  const updateConsent = (key, val) => setForm((f) => ({ ...f, consents: { ...f.consents, [key]: val } }));
  const updateFounder = (idx, key, val) =>
    setForm((f) => ({ ...f, founders: f.founders.map((fn, i) => i === idx ? { ...fn, [key]: val } : fn) }));
  const addFounder    = () => setForm((f) => ({ ...f, founders: [...f.founders, emptyFounder()] }));
  const removeFounder = (idx) =>
    setForm((f) => ({ ...f, founders: f.founders.length > 1 ? f.founders.filter((_, i) => i !== idx) : f.founders }));

  const touch = (key) => setTouched((t) => ({ ...t, [key]: true }));

  /* ── live errors for current step ─────────────────────────── */
  const stepErrs = useMemo(
    () => validateByStep(wizardStep, form, deck),
    [wizardStep, form, deck],
  );

  /** Returns whether to show the error for a given field key */
  const showErr = (key) => (attempted || touched[key]) && !!stepErrs[key];

  /** Input className helper */
  const ic = (key, hasValue) => fieldClass("wit-input", showErr(key), hasValue && !stepErrs[key]);

  const allConsents = useMemo(() => Object.values(form.consents).every(Boolean), [form.consents]);

  /* ── navigation ─────────────────────────────────────────────── */
  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const handleNext = () => {
    if (Object.keys(stepErrs).length) {
      setAttempted(true);
      setState({ status: "error", message: t("submit.errorGeneric") });
      return;
    }
    setAttempted(false);
    setTouched({});
    setState({ status: "idle", message: "" });
    setWizardStep((s) => s + 1);
    scrollTop();
  };

  const handleBack = () => {
    setAttempted(false);
    setTouched({});
    setState({ status: "idle", message: "" });
    setWizardStep((s) => s - 1);
    scrollTop();
  };

  /* ── submit ──────────────────────────────────────────────────── */
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!allConsents) {
      setState({ status: "error", message: t("submit.errorConsent") });
      return;
    }
    if (!deck) {
      setState({ status: "error", message: t("submit.errorDeck") });
      return;
    }
    setState({ status: "loading", message: "" });
    const num = (v) => (v === "" || v == null ? null : Number(v));
    const payload = {
      company: {
        name: form.company.name.trim(),
        website: form.company.website.trim(),
        linkedin: form.company.linkedin?.trim() || null,
        founded_year: Number(form.company.founded_year),
        country: form.company.country,
        city: form.company.city.trim(),
        sectors: form.company.sectors,
        one_liner: form.company.one_liner.trim(),
        description: form.company.description.trim(),
      },
      founders: form.founders.map((fn) => ({
        first_name: fn.first_name.trim(), last_name: fn.last_name.trim(),
        email: fn.email.trim().toLowerCase(), linkedin: fn.linkedin?.trim() || null,
        role: fn.role.trim(), technical: fn.technical, female_led: fn.female_led,
        diverse_team: fn.diverse_team, previous_startup_experience: fn.previous_startup_experience,
      })),
      details: form.details,
      funding: {
        ask_amount: Number(form.funding.ask_amount), ask_currency: form.funding.ask_currency,
        raised_so_far: num(form.funding.raised_so_far), valuation: num(form.funding.valuation),
        runway_months: num(form.funding.runway_months), mrr: num(form.funding.mrr),
        arr: num(form.funding.arr), has_pilot_customers: !!form.funding.has_pilot_customers,
        customer_count: num(form.funding.customer_count), use_of_funds: form.funding.use_of_funds?.trim() || null,
      },
      consents: form.consents,
      language: i18n.language?.startsWith("en") ? "en" : "de",
    };
    try {
      const res = await api.post("/startups/submit", payload);
      const { startup_id, status_token } = res.data;
      const fd = new FormData();
      fd.append("file", deck);
      await api.post(`/startups/${startup_id}/deck`, fd, {
        headers: { "Content-Type": "multipart/form-data", "X-Status-Token": status_token },
      });
      setResult(res.data);
      setState({ status: "success", message: "" });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      let message = t("submit.errorGeneric");
      if (err?.response?.status === 429) message = t("submit.errorRateLimit");
      if (err?.response?.status === 413) message = t("submit.deckErrorSize", { mb: 25 });
      setState({ status: "error", message });
    }
  };

  /* ── Success ─────────────────────────────────────────────────── */
  if (state.status === "success" && result) {
    return (
      <div data-testid="submit-success">
        <VisualSection testId="submit-success-card">
          <Reveal>
            <div className="max-w-2xl mx-auto wit-card text-center flex flex-col items-center gap-6">
              <div className="w-14 h-14 rounded-full bg-raspberry text-white flex items-center justify-center">
                <Check size={22} />
              </div>
              <SectionHeading title={t("submit.successTitle")} subtitle={t("submit.successBody")} align="center" />
              <Link to="/" className="wit-btn-primary mt-2" data-testid="submit-success-home">
                {t("submit.successSecondaryCta")} <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </VisualSection>
      </div>
    );
  }

  /* ── Form ─────────────────────────────────────────────────────── */
  return (
    <div data-testid="page-submit-startup">
      <PageHero
        eyebrow={t("submit.eyebrow")} title={t("submit.title")} subtitle={t("submit.lead")}
        image={ASSETS.editorial[3]} testId="submit-hero"
      />

      <div className="wit-section-wide pb-24">
        <WizardProgress wizardStep={wizardStep} steps={wizardSteps} />
        <p className="wit-eyebrow text-warmGrey mb-6" data-testid="wizard-step-label">
          {t("submit.wizard.stepOf", { current: wizardStep, total: TOTAL_STEPS })}
        </p>

        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-10 w-full" data-testid="submit-form">

          {/* ══ STEP 1 — UNTERNEHMEN ══ */}
          {wizardStep === 1 && (
            <section className="flex flex-col gap-6" data-testid="section-company">
              <SectionBanner
                eyebrow={t("submit.sectionACompany")} title={t("submit.sectionACompany")}
                hint={t("submit.sectionACompanyHint")} accent="bg-roseMilk"
              />
              <div className="wit-card grid sm:grid-cols-2 gap-5">

                <Field label={t("submit.companyName")} required
                  error={showErr("company.name") ? stepErrs["company.name"] : null}
                  testId="field-name">
                  <input
                    className={ic("company.name", !!form.company.name.trim())}
                    value={form.company.name}
                    onChange={(e) => updateCompany("name", e.target.value)}
                    onBlur={() => touch("company.name")}
                    data-testid="input-company-name"
                  />
                </Field>

                <Field label={t("submit.website")} required
                  error={showErr("company.website") ? stepErrs["company.website"] : null}
                  testId="field-website">
                  <input
                    className={ic("company.website", !!form.company.website.trim() && !stepErrs["company.website"])}
                    type="url" placeholder="https://..."
                    value={form.company.website}
                    onChange={(e) => updateCompany("website", e.target.value)}
                    onBlur={() => touch("company.website")}
                    data-testid="input-website"
                  />
                </Field>

                <Field label={t("submit.linkedin")} testId="field-linkedin">
                  <input className="wit-input" type="url" placeholder="https://linkedin.com/company/..."
                    value={form.company.linkedin}
                    onChange={(e) => updateCompany("linkedin", e.target.value)}
                    data-testid="input-linkedin"
                  />
                </Field>

                <Field label={t("submit.foundedYear")} required
                  error={showErr("company.founded_year") ? stepErrs["company.founded_year"] : null}
                  testId="field-founded">
                  <input
                    className={ic("company.founded_year", !!form.company.founded_year && !stepErrs["company.founded_year"])}
                    type="number" min="1990" max="2030"
                    value={form.company.founded_year}
                    onChange={(e) => updateCompany("founded_year", e.target.value)}
                    onBlur={() => touch("company.founded_year")}
                    data-testid="input-founded"
                  />
                </Field>

                <Field label={t("submit.country")} required
                  error={showErr("company.country") ? stepErrs["company.country"] : null}
                  testId="field-country">
                  <select
                    className={ic("company.country", !!form.company.country)}
                    value={form.company.country}
                    onChange={(e) => updateCompany("country", e.target.value)}
                    onBlur={() => touch("company.country")}
                    data-testid="input-country"
                  >
                    <option value="">{t("submit.countryPlaceholder")}</option>
                    {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>

                <Field label={t("submit.city")} required
                  error={showErr("company.city") ? stepErrs["company.city"] : null}
                  testId="field-city">
                  <input
                    className={ic("company.city", !!form.company.city.trim())}
                    value={form.company.city}
                    onChange={(e) => updateCompany("city", e.target.value)}
                    onBlur={() => touch("company.city")}
                    data-testid="input-city"
                  />
                </Field>

                <Field label={t("submit.sectors")} required hint={t("submit.sectorsHint")}
                  error={showErr("company.sectors") ? stepErrs["company.sectors"] : null}
                  span={2} testId="field-sectors">
                  <MultiSelect
                    options={SECTORS.map((s) => ({ key: s, label: s }))}
                    value={form.company.sectors}
                    onChange={(v) => { updateCompany("sectors", v); touch("company.sectors"); }}
                    testId="ms-sectors"
                  />
                </Field>

                <Field label={t("submit.oneLiner")} required hint={t("submit.oneLinerHint")}
                  error={showErr("company.one_liner") ? stepErrs["company.one_liner"] : null}
                  span={2} testId="field-oneliner">
                  <input
                    className={ic("company.one_liner", !!form.company.one_liner.trim())}
                    maxLength={140}
                    value={form.company.one_liner}
                    onChange={(e) => updateCompany("one_liner", e.target.value)}
                    onBlur={() => touch("company.one_liner")}
                    data-testid="input-oneliner"
                  />
                </Field>

                <Field label={t("submit.description")} required hint={t("submit.descriptionHint")}
                  error={showErr("company.description") ? stepErrs["company.description"] : null}
                  span={2} testId="field-description">
                  <textarea
                    rows={6} maxLength={1500}
                    className={ic("company.description", !!form.company.description.trim() && !stepErrs["company.description"]) + " resize-none"}
                    value={form.company.description}
                    onChange={(e) => updateCompany("description", e.target.value)}
                    onBlur={() => touch("company.description")}
                    data-testid="input-description"
                  />
                  <span className="text-xs text-warmGrey">
                    {form.company.description.length} / 1500
                    {form.company.description.length < 100 && form.company.description.length > 0 && (
                      <span className="text-red-500 ml-2">
                        (noch {100 - form.company.description.length} Zeichen)
                      </span>
                    )}
                  </span>
                </Field>

              </div>
            </section>
          )}

          {/* ══ STEP 2 — FOUNDER ══ */}
          {wizardStep === 2 && (
            <section className="flex flex-col gap-6" data-testid="section-founders">
              <SectionBanner
                eyebrow={t("submit.sectionBFounders")} title={t("submit.sectionBFounders")}
                hint={t("submit.sectionBFoundersHint")} accent="bg-lavenderFog"
              />
              {form.founders.map((fn, idx) => (
                <div key={idx} className="wit-card" data-testid={`founder-card-${idx}`}>
                  <div className="flex items-center justify-between mb-5">
                    <p className="wit-eyebrow text-raspberry">{t("submit.founderIndex", { n: idx + 1 })}</p>
                    {form.founders.length > 1 && (
                      <button type="button" onClick={() => removeFounder(idx)}
                        className="text-sm text-warmGrey hover:text-coral flex items-center gap-1"
                        data-testid={`founder-remove-${idx}`}>
                        <X size={14} /> {t("submit.removeFounder")}
                      </button>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">

                    <Field label={t("submit.firstName")} required
                      error={showErr(`founder.${idx}.first_name`) ? stepErrs[`founder.${idx}.first_name`] : null}
                      testId={`founder-first-${idx}`}>
                      <input
                        className={ic(`founder.${idx}.first_name`, !!fn.first_name.trim())}
                        value={fn.first_name}
                        onChange={(e) => updateFounder(idx, "first_name", e.target.value)}
                        onBlur={() => touch(`founder.${idx}.first_name`)}
                        data-testid={`input-founder-first-${idx}`}
                      />
                    </Field>

                    <Field label={t("submit.lastName")} required
                      error={showErr(`founder.${idx}.last_name`) ? stepErrs[`founder.${idx}.last_name`] : null}
                      testId={`founder-last-${idx}`}>
                      <input
                        className={ic(`founder.${idx}.last_name`, !!fn.last_name.trim())}
                        value={fn.last_name}
                        onChange={(e) => updateFounder(idx, "last_name", e.target.value)}
                        onBlur={() => touch(`founder.${idx}.last_name`)}
                        data-testid={`input-founder-last-${idx}`}
                      />
                    </Field>

                    <Field label={t("submit.founderEmail")} required
                      error={showErr(`founder.${idx}.email`) ? stepErrs[`founder.${idx}.email`] : null}
                      testId={`founder-email-${idx}`}>
                      <input
                        className={ic(`founder.${idx}.email`, !!fn.email.trim() && !stepErrs[`founder.${idx}.email`])}
                        type="email"
                        value={fn.email}
                        onChange={(e) => updateFounder(idx, "email", e.target.value)}
                        onBlur={() => touch(`founder.${idx}.email`)}
                        data-testid={`input-founder-email-${idx}`}
                      />
                    </Field>

                    <Field label={t("submit.founderRole")} required
                      error={showErr(`founder.${idx}.role`) ? stepErrs[`founder.${idx}.role`] : null}
                      testId={`founder-role-${idx}`}>
                      <input
                        className={ic(`founder.${idx}.role`, !!fn.role.trim())}
                        placeholder="CEO, CTO, CPO ..."
                        value={fn.role}
                        onChange={(e) => updateFounder(idx, "role", e.target.value)}
                        onBlur={() => touch(`founder.${idx}.role`)}
                        data-testid={`input-founder-role-${idx}`}
                      />
                    </Field>

                    <Field label={t("submit.linkedinOptional")} span={2} testId={`founder-linkedin-${idx}`}>
                      <input className="wit-input" type="url" placeholder="https://linkedin.com/in/..."
                        value={fn.linkedin}
                        onChange={(e) => updateFounder(idx, "linkedin", e.target.value)}
                        data-testid={`input-founder-linkedin-${idx}`}
                      />
                    </Field>

                  </div>
                  <div className="grid sm:grid-cols-2 gap-3 mt-5">
                    {[
                      ["technical", "technical"],
                      ["female_led", "femaleLed"],
                      ["diverse_team", "diverseTeam"],
                      ["previous_startup_experience", "previousExperience"],
                    ].map(([key, label]) => (
                      <label key={key} className="flex items-center gap-3 bg-porcelain rounded-2xl px-4 py-3 border border-sandBeige text-sm text-cocoa cursor-pointer">
                        <input type="checkbox" className="accent-raspberry"
                          checked={fn[key]}
                          onChange={(e) => updateFounder(idx, key, e.target.checked)}
                          data-testid={`founder-${idx}-${key}`}
                        />
                        <span>{t(`submit.${label}`)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              <div>
                <button type="button" onClick={addFounder} className="wit-btn-secondary" data-testid="add-founder">
                  <Plus size={14} /> {t("submit.addFounder")}
                </button>
              </div>
            </section>
          )}

          {/* ══ STEP 3 — STAGE & FUNDING ══ */}
          {wizardStep === 3 && (
            <div className="flex flex-col gap-10">
              <section className="flex flex-col gap-6" data-testid="section-details">
                <SectionBanner
                  eyebrow={t("submit.sectionCDetails")} title={t("submit.sectionCDetails")}
                  hint={t("submit.sectionCDetailsHint")} accent="bg-sageMint"
                />
                <div className="wit-card flex flex-col gap-6">
                  <Field label={t("submit.stage")} required testId="field-stage">
                    <div className="flex flex-wrap gap-2" data-testid="stage-radios">
                      {STAGES.map((s) => {
                        const labelKey = "stage" + s.split("_").map(p => p[0].toUpperCase() + p.slice(1)).join("");
                        const selected = form.details.stage === s;
                        return (
                          <button type="button" key={s} onClick={() => updateDetails("stage", s)}
                            className={`wit-pill transition-colors ${selected ? "bg-raspberry text-white border-raspberry" : "bg-white border-sandBeige text-cocoa hover:border-raspberry"}`}
                            data-testid={`stage-${s}`}>
                            {t(`submit.${labelKey}`)}
                          </button>
                        );
                      })}
                    </div>
                  </Field>

                  <Field label={t("submit.businessModels")} required hint={t("submit.businessModelsHint")}
                    error={showErr("details.business_models") ? stepErrs["details.business_models"] : null}
                    testId="field-business-models">
                    <MultiSelect
                      options={BUSINESS_MODELS.map((s) => ({ key: s, label: s }))}
                      value={form.details.business_models}
                      onChange={(v) => { updateDetails("business_models", v); touch("details.business_models"); }}
                      testId="ms-business-models"
                    />
                  </Field>
                </div>
              </section>

              <section className="flex flex-col gap-6" data-testid="section-funding">
                <SectionBanner
                  eyebrow={t("submit.sectionDFunding")} title={t("submit.sectionDFunding")}
                  hint={t("submit.sectionDFundingHint")} accent="bg-butterCream"
                />
                <div className="wit-card grid sm:grid-cols-2 gap-5">

                  <Field label={t("submit.fundingAsk")} required
                    error={showErr("funding.ask_amount") ? stepErrs["funding.ask_amount"] : null}
                    testId="field-ask">
                    <input
                      className={ic("funding.ask_amount", !!form.funding.ask_amount && !stepErrs["funding.ask_amount"])}
                      type="number" min="0"
                      value={form.funding.ask_amount}
                      onChange={(e) => updateFunding("ask_amount", e.target.value)}
                      onBlur={() => touch("funding.ask_amount")}
                      data-testid="input-ask-amount"
                    />
                  </Field>

                  <Field label={t("submit.currency")} required testId="field-currency">
                    <select className="wit-input" value={form.funding.ask_currency}
                      onChange={(e) => updateFunding("ask_currency", e.target.value)}
                      data-testid="input-currency">
                      {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>

                  <Field label={t("submit.raisedSoFar")} testId="field-raised">
                    <input className="wit-input" type="number" min="0" value={form.funding.raised_so_far}
                      onChange={(e) => updateFunding("raised_so_far", e.target.value)} data-testid="input-raised" />
                  </Field>
                  <Field label={t("submit.valuation")} testId="field-valuation">
                    <input className="wit-input" type="number" min="0" value={form.funding.valuation}
                      onChange={(e) => updateFunding("valuation", e.target.value)} data-testid="input-valuation" />
                  </Field>
                  <Field label={t("submit.runwayMonths")} testId="field-runway">
                    <input className="wit-input" type="number" min="0" max="240" value={form.funding.runway_months}
                      onChange={(e) => updateFunding("runway_months", e.target.value)} data-testid="input-runway" />
                  </Field>
                  <Field label={t("submit.mrr")} testId="field-mrr">
                    <input className="wit-input" type="number" min="0" value={form.funding.mrr}
                      onChange={(e) => updateFunding("mrr", e.target.value)} data-testid="input-mrr" />
                  </Field>
                  <Field label={t("submit.arr")} testId="field-arr">
                    <input className="wit-input" type="number" min="0" value={form.funding.arr}
                      onChange={(e) => updateFunding("arr", e.target.value)} data-testid="input-arr" />
                  </Field>
                  <Field label={t("submit.customerCount")} testId="field-customers">
                    <input className="wit-input" type="number" min="0" value={form.funding.customer_count}
                      onChange={(e) => updateFunding("customer_count", e.target.value)} data-testid="input-customers" />
                  </Field>

                  <label className="flex items-center gap-3 bg-porcelain rounded-2xl px-4 py-3 border border-sandBeige text-sm text-cocoa cursor-pointer sm:col-span-2">
                    <input type="checkbox" className="accent-raspberry"
                      checked={form.funding.has_pilot_customers}
                      onChange={(e) => updateFunding("has_pilot_customers", e.target.checked)}
                      data-testid="input-has-pilot"
                    />
                    <span>{t("submit.hasPilot")}</span>
                  </label>

                  <Field label={t("submit.useOfFunds")} hint={t("submit.useOfFundsHint")} span={2} testId="field-use-of-funds">
                    <textarea rows={4} maxLength={2000} className="wit-input resize-none"
                      value={form.funding.use_of_funds}
                      onChange={(e) => updateFunding("use_of_funds", e.target.value)}
                      data-testid="input-use-of-funds"
                    />
                  </Field>

                </div>
              </section>
            </div>
          )}

          {/* ══ STEP 4 — PITCH DECK ══ */}
          {wizardStep === 4 && (
            <section className="flex flex-col gap-6" data-testid="section-deck">
              <SectionBanner
                eyebrow={t("submit.sectionEDeck")} title={t("submit.sectionEDeck")}
                hint={t("submit.sectionEDeckHint", { mb: 25 })} accent="bg-skyMist"
              />
              <PitchDeckDropzone
                file={deck} maxMb={25}
                onChange={(f, err) => { setDeck(f); setDeckError(err); }}
              />
              {deckError && <p className="text-sm text-coral" data-testid="deck-error">{deckError}</p>}
              {showErr("deck") && !deck && (
                <p className="text-sm text-red-600 flex items-center gap-1.5" data-testid="deck-required-error">
                  {stepErrs["deck"]}
                </p>
              )}
            </section>
          )}

          {/* ══ STEP 5 — CONSENTS ══ */}
          {wizardStep === 5 && (
            <div className="flex flex-col gap-10">
              <section className="flex flex-col gap-6" data-testid="section-consents">
                <SectionBanner
                  eyebrow={t("submit.sectionFConsent")} title={t("submit.sectionFConsent")}
                  hint={t("submit.sectionFConsentHint")} accent="bg-roseMilk"
                />
                <div className="wit-card flex flex-col gap-3">
                  {[
                    ["pitch_deck_upload", "consentPitch"],
                    ["privacy_policy",    "consentPrivacy", true],
                    ["ai_analysis",       "consentAI"],
                    ["no_auto_forward",   "consentNoForward"],
                    ["no_guarantee",      "consentNoGuarantee"],
                    ["gdpr_data_processing", "consentGDPR"],
                    ["ip_ownership",      "consentIP"],
                    ["nda_confidentiality","consentNDA"],
                  ].map(([key, labelKey, withLink]) => (
                    <label key={key}
                      className={`flex items-start gap-3 rounded-2xl px-4 py-3 border text-sm text-cocoa cursor-pointer transition-colors ${
                        state.status === "error" && !form.consents[key]
                          ? "bg-red-50 border-red-300"
                          : form.consents[key]
                          ? "bg-green-50 border-green-300"
                          : "bg-porcelain border-sandBeige"
                      }`}>
                      <input type="checkbox" className="accent-raspberry mt-1"
                        checked={!!form.consents[key]}
                        onChange={(e) => updateConsent(key, e.target.checked)}
                        data-testid={`consent-${key}`}
                      />
                      <span className="flex-1 leading-relaxed">
                        {withLink ? (
                          <Trans i18nKey={`submit.${labelKey}`}
                            components={[<Link key="0" to="/privacy" className="underline hover:text-raspberry">privacy</Link>]} />
                        ) : (
                          t(`submit.${labelKey}`)
                        )}
                      </span>
                    </label>
                  ))}
                  {state.status === "error" && !allConsents && (
                    <p className="text-sm text-red-600 flex items-center gap-1.5 mt-1" role="alert" data-testid="consents-error">
                      {t("submit.errorConsent")}
                    </p>
                  )}
                </div>
              </section>

              <section>
                <div className="wit-card flex flex-col sm:flex-row gap-5 items-start bg-sageMint/60 border-sageMint">
                  <div className="w-12 h-12 rounded-2xl bg-white border border-cardEdge flex items-center justify-center text-raspberry shrink-0">
                    <ShieldCheck size={20} strokeWidth={1.6} />
                  </div>
                  <div>
                    <p className="font-serif text-xl text-cocoa">{t("submit.trustTitle")}</p>
                    <p className="mt-2 text-sm text-cocoa/80 leading-relaxed">{t("submit.trustBody")}</p>
                  </div>
                </div>
              </section>
            </div>
          )}

          {/* ══ Global error ══ */}
          {state.status === "error" && state.message && (
            <p className="text-coral text-sm" role="alert" data-testid="submit-error">
              {state.message}
            </p>
          )}

          {/* ══ Navigation ══ */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-2">
            {wizardStep > 1 && (
              <button type="button" onClick={handleBack} className="wit-btn-secondary" data-testid="wizard-back">
                <ArrowLeft size={16} /> {t("submit.wizard.back")}
              </button>
            )}
            {wizardStep < TOTAL_STEPS ? (
              <button type="button" onClick={handleNext} className="wit-btn-primary" data-testid="wizard-next">
                {t("submit.wizard.next")} <ArrowRight size={16} />
              </button>
            ) : (
              <button type="submit" disabled={state.status === "loading"}
                className="wit-btn-primary disabled:opacity-60" data-testid="submit-button">
                {state.status === "loading" ? t("submit.submitting") : <>{t("submit.submitButton")} <ArrowRight size={16} /></>}
              </button>
            )}
            {wizardStep === 1 && (
              <button type="button" onClick={() => navigate("/startups")}
                className="wit-btn-secondary" data-testid="submit-cancel">
                {t("comingSoon.back")}
              </button>
            )}
          </div>

        </form>
      </div>
    </div>
  );
}
